import { h } from '@atomify/jsx';

import { BPDFunctionalSvg } from '../..';

export const ChipCheckbox = ({
    validationTypes,
    validationRegex,
    validationErrorMessage,
    requiredErrorMessage,
    value,
    classes,
    disabled,
    name,
    id,
    required,
    checked,
    hook,
    attr,
    label,
    icon,
    error,
    displayName,
    variant,
    hideError = false,
    collapsedLabel = false,
}: {
    validationTypes?: string | null;
    validationRegex?: string | null;
    validationErrorMessage?: string | null;
    requiredErrorMessage?: string | null;
    value?: any;
    classes?: string;
    disabled?: boolean;
    name: string;
    id: string;
    required?: boolean;
    checked?: boolean;
    hook?: string;
    attr?: { [key: string]: string };
    label: string | Text;
    icon?: string;
    error?: string;
    srOnly?: boolean;
    displayName?: string | null;
    hideError?: boolean;
    variant?: 'small';
    collapsedLabel?: boolean;
}) => {
    const inputClasses: Record<string, boolean | undefined> = {
        ['c-chip']: true,
        [`chip--${variant}`]: !!variant,
        ['chip--collapsed']: collapsedLabel,
        ['chip--icon']: !!icon,
        ['chip--text-only']: !icon,
        ['form__item']: true,
        ['form__item--disabled']: disabled,
    };

    return (
        <bpd-input
            validation-types={validationTypes}
            validation-regex={validationRegex}
            validation-error-message={validationErrorMessage}
            required-error-message={requiredErrorMessage}
            bindValue={value}
            class={classes}
            displayName={displayName}>
            <ChipInput
                name={name}
                inputClasses={inputClasses}
                id={id}
                required={required}
                disabled={disabled}
                checked={checked}
                value={value}
                attr={attr}
                icon={icon}
                label={label}
                hook={hook}
                type="checkbox"
            />

            {!hideError && (
                <div class="form__item-error" role="alert">
                    {error}
                </div>
            )}
        </bpd-input>
    );
};

export const ChipRadio = ({
    validationTypes,
    validationRegex,
    validationErrorMessage,
    requiredErrorMessage,
    value,
    classes,
    disabled,
    required,
    name,
    displayName,
    hook,
    variant,
    options,
    collapsedLabel,
}: {
    validationTypes?: string | null;
    validationRegex?: string | null;
    validationErrorMessage?: string | null;
    requiredErrorMessage?: string | null;
    value?: any;
    classes?: string;
    disabled?: boolean;
    name: string;
    required?: boolean;
    displayName?: string | null;
    variant?: 'small';
    hook?: string;
    collapsedLabel?: boolean;
    options: Array<{
        id: string;
        checked?: boolean;
        hook?: string;
        attr?: string[];
        label?: string | Text;
        value: string;
        icon?: string;
    }>;
}) => {
    return (
        <bpd-radio
            validation-types={validationTypes}
            validation-regex={validationRegex}
            validation-error-message={validationErrorMessage}
            required-error-message={requiredErrorMessage}
            bindValue={value}
            class={classes}
            displayName={displayName}
            {...{
                [`js-hook-${hook}`]: !!hook,
            }}>
            <div class="chip__radio-container">
                {options.map((option) => {
                    const inputClasses: Record<string, boolean | undefined> = {
                        ['c-chip']: true,
                        [`chip--${variant}`]: !!variant,
                        ['chip--collapsed']: collapsedLabel,
                        ['chip--icon']: !!option.icon,
                        ['chip--text-only']: !option.icon,
                        ['form__item']: true,
                        ['form__item--disabled']: disabled,
                    };

                    return (
                        <div class="chip__radio">
                            <ChipInput
                                name={name}
                                inputClasses={inputClasses}
                                id={option.id}
                                required={required}
                                disabled={disabled}
                                checked={option.checked}
                                value={option.value}
                                attr={option.attr}
                                icon={option.icon}
                                label={option.label}
                                hook={option.hook}
                                type="radio"
                            />
                        </div>
                    );
                })}
            </div>
        </bpd-radio>
    );
};

const ChipInput = ({
    name,
    inputClasses,
    id,
    required,
    disabled,
    checked,
    value,
    attr,
    icon,
    label,
    hook,
    type,
}: {
    name: string;
    inputClasses: Record<string, boolean | undefined>;
    id: string;
    required?: boolean;
    disabled?: boolean;
    checked?: boolean;
    value?: string;
    attr?: Record<string, any | undefined>;
    hook?: string;
    icon?: string;
    label?: string | Text;
    type: 'checkbox' | 'radio';
}) => (
    <div class={inputClasses}>
        <div class="chip__wrapper">
            <input
                class="chip__input u-sr-only"
                type={type}
                name={name}
                id={id}
                required={required}
                disabled={disabled}
                checked={checked}
                value={value}
                {...{ ...attr, ...(hook && { [`js-hook-${hook}`]: true }) }}
            />

            <label class="chip__label" for={id}>
                <span class="chip__button">
                    {icon && (
                        <span class="chip__icon">
                            <BPDFunctionalSvg name={icon} />
                        </span>
                    )}

                    {!icon && <span class="chip__title">{label}</span>}
                </span>
            </label>

            {icon && <span class="chip__title">{label}</span>}
        </div>
    </div>
);
